import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
    armWillChange,
    disarmWillChange,
    injectFoucStyles,
    isGsapDone,
    markGsapDone,
    markGsapReady,
    parseNumericDataAttr,
    prefersReducedMotion,
} from './_shared';

const SELECTOR = '[data-mda-gsap-reveal]';
const ITEM_SELECTOR = '[data-mda-gsap-reveal-item]';
const REVEAL_TYPES = [
    'fade',
    'fade-up',
    'fade-down',
    'fade-left',
    'fade-right',
    'zoom-in',
];
const DEFAULT_TYPE = 'fade-up';
const DEFAULT_Y = 40;
const DEFAULT_X = 40;
const DEFAULT_DURATION = 0.8;
const DEFAULT_DELAY = 0;

function getRevealItems(container) {
    const items = container.querySelectorAll(ITEM_SELECTOR);

    if (items.length) {
        return gsap.utils.toArray(items);
    }

    return [container];
}

function getRevealType(container, options = {}) {
    const raw = options.type ?? container?.dataset?.mdaGsapReveal ?? '';
    const type = raw === '' || raw === 'true' ? DEFAULT_TYPE : raw;

    return REVEAL_TYPES.includes(type) ? type : DEFAULT_TYPE;
}

function getRevealVars(container, options = {}) {
    const type = getRevealType(container, options);
    const y = parseNumericDataAttr(container, 'mdaGsapRevealY', DEFAULT_Y);
    const x = parseNumericDataAttr(container, 'mdaGsapRevealX', DEFAULT_X);

    const presets = {
        fade: {
            from: { autoAlpha: 0 },
            to: { autoAlpha: 1 },
            clearProps: '',
        },
        'fade-up': {
            from: { autoAlpha: 0, y },
            to: { autoAlpha: 1, y: 0 },
            clearProps: 'transform',
        },
        'fade-down': {
            from: { autoAlpha: 0, y: -y },
            to: { autoAlpha: 1, y: 0 },
            clearProps: 'transform',
        },
        'fade-left': {
            from: { autoAlpha: 0, x: -x },
            to: { autoAlpha: 1, x: 0 },
            clearProps: 'transform',
        },
        'fade-right': {
            from: { autoAlpha: 0, x },
            to: { autoAlpha: 1, x: 0 },
            clearProps: 'transform',
        },
        'zoom-in': {
            from: {
                autoAlpha: 0,
                scale: 0.88,
                transformOrigin: '50% 50%',
            },
            to: { autoAlpha: 1, scale: 1 },
            clearProps: 'transform',
        },
    };

    return { type, ...presets[type] };
}

function getRevealOptions(container, options = {}) {
    return {
        duration: Number.isFinite(options.duration)
            ? options.duration
            : parseNumericDataAttr(
                  container,
                  'mdaGsapRevealDuration',
                  DEFAULT_DURATION
              ),
        delay: Number.isFinite(options.delay)
            ? options.delay
            : parseNumericDataAttr(
                  container,
                  'mdaGsapRevealDelay',
                  DEFAULT_DELAY
              ),
    };
}

function prepareReveal(items, container, options = {}) {
    const { from } = getRevealVars(container, options);

    gsap.set(items, from);
    markGsapReady(container);
}

function runReveal(items, container, options = {}, scrollTriggerConfig = null) {
    if (isGsapDone(container)) {
        return null;
    }

    const revealOptions = getRevealOptions(container, options);
    const { from, to, clearProps } = getRevealVars(container, options);
    const targets = gsap.utils.toArray(items);

    if (scrollTriggerConfig) {
        gsap.set(targets, from);
        markGsapReady(container);
    }

    return gsap.fromTo(targets, from, {
        ...to,
        duration: revealOptions.duration,
        delay: revealOptions.delay,
        ease: 'power2.out',
        overwrite: 'auto',
        ...(clearProps ? { clearProps } : {}),
        ...(scrollTriggerConfig ? { scrollTrigger: scrollTriggerConfig } : {}),
        onStart() {
            armWillChange(targets);
            gsap.set(targets, { transition: 'none' });
        },
        onComplete() {
            gsap.set(targets, { clearProps: 'transition' });
            disarmWillChange(targets);
            markGsapDone(container);
        },
    });
}

export class ScrollReveal {
    static selector = SELECTOR;

    constructor(element, options = {}) {
        this.element = element;
        this.options = options;
        this.tween = null;
        this.batchTrigger = null;

        if (prefersReducedMotion() || !element) {
            return;
        }

        this.create();
    }

    create() {
        const items = getRevealItems(this.element);

        this.tween = runReveal(items, this.element, this.options, {
            trigger: this.element,
            start: 'top 85%',
            once: true,
            toggleActions: 'play play play none',
        });
    }

    destroy() {
        this.batchTrigger?.kill();
        this.batchTrigger = null;
        this.tween?.scrollTrigger?.kill();
        this.tween?.kill();
        this.tween = null;
    }

    static init(root = document, options = {}) {
        if (prefersReducedMotion() || !root?.querySelectorAll) {
            return [];
        }

        injectFoucStyles();

        const containers = Array.from(root.querySelectorAll(SELECTOR));

        if (!containers.length) {
            return [];
        }

        containers.forEach(container => {
            prepareReveal(getRevealItems(container), container, options);
        });

        const handleRevealBatch = batch => {
            batch.forEach(container => {
                runReveal(getRevealItems(container), container, options);
            });
        };

        const batchTrigger = ScrollTrigger.batch(SELECTOR, {
            start: 'top 85%',
            once: true,
            fastScrollEnd: true,
            onEnter: handleRevealBatch,
            onEnterBack: handleRevealBatch,
        });

        return [
            {
                elements: containers,
                batchTrigger,
                destroy() {
                    (Array.isArray(batchTrigger)
                        ? batchTrigger
                        : [batchTrigger]
                    ).forEach(trigger => trigger?.kill?.());

                    gsap.killTweensOf(
                        containers.flatMap(container =>
                            getRevealItems(container)
                        )
                    );
                },
            },
        ];
    }
}
